// One persona up close: their sandbox browser (live view or last screenshot),
// the app task they are working on step by step, their thought stream, wake
// schedule with System-1 verdicts, inbox, chat lines and character sheet.
import { useState } from 'react';
import { Link } from 'wouter';
import { api } from '../../convex/_generated/api';
import type { Doc, Id } from '../../convex/_generated/dataModel';
import { PERSONAS } from '../../convex/shared/scenario';
import { EmailList } from '../components/Email';
import { FeedList } from '../components/Feed';
import { LiveScreen } from '../components/LiveScreen';
import { ActivityChip, PersonaAvatar } from '../components/persona';
import { fmtClock, fmtDuration, fmtRelative, fmtUsdPrecise, humanize, shortName } from '../lib/format';
import { useCast, useNow, type Cast } from '../lib/hooks';
import { readLocal, STORAGE_KEYS, writeLocal } from '../lib/storage';
import { useSession, useSimMutation, useSimQuery } from '../lib/session';
import { Icon } from '../ui/Icon';
import { Button, Chip, Empty, Meter, Panel, Segmented, Spinner } from '../ui/primitives';
import { useToasts } from '../ui/Toasts';

type Tab = 'mind' | 'wakes' | 'inbox' | 'chat' | 'sheet';

export function AgentView({ personaKey }: { personaKey: string }) {
  const cast = useCast();
  const card = cast?.byKey.get(personaKey);
  if (cast === undefined) return <div className="page-loading"><Spinner /></div>;
  if (!card) {
    return (
      <div className="page">
        <Empty icon="people" title="Nobody by that name">
          <Link href="/cast">Back to the cast</Link>
        </Empty>
      </div>
    );
  }
  return <AgentDetail personaId={card._id} cast={cast} />;
}

function AgentDetail({ personaId, cast }: { personaId: Id<'personas'>; cast: Cast }) {
  const now = useNow(5_000);
  const card = cast.byId.get(personaId)!;
  const persona = useSimQuery(api.agents.personas.getPersona, { personaId });
  const sessions = useSimQuery(api.browser.sessions.listLiveSessions, {});
  const tasks = useSimQuery(api.browser.tasks.listTasks, { personaId, limit: 12 });
  const session = sessions?.find((s) => s.personaId === personaId && s.purpose === 'app');
  const live = session?.status === 'active' || session?.status === 'starting';
  const [tab, setTab] = useState<Tab>('mind');
  const [taskId, setTaskId] = useState<Id<'appTasks'> | null>(null);
  const activeTask = tasks?.find((t) => t._id === (taskId ?? session?.currentTaskId)) ?? tasks?.[0];

  return (
    <div className="page agent-page">
      <header className="agent-head panel">
        <PersonaAvatar card={card} size={88} live={live} />
        <div className="agent-id">
          <h1>{card.name}</h1>
          <p>{card.title}</p>
          <div className="agent-chips">
            <ActivityChip activity={card.activity} />
            <Chip icon={card.breakpoint === 'mobile' ? 'mobile' : 'desktop'}>{card.breakpoint === 'mobile' ? 'Phone' : 'Desktop'}</Chip>
            {persona ? <Chip icon="key">aa-app {persona.aaApp.role}</Chip> : null}
            <Chip tone={card.aaAppStatus === 'active' ? 'leaf' : card.aaAppStatus === 'locked_out' ? 'danger' : 'neutral'}>{humanize(card.aaAppStatus)}</Chip>
            {!card.enabled ? <Chip tone="danger">Benched</Chip> : null}
          </div>
          {card.currentPlan ? (
            <p className="agent-plan">
              <Icon name="flag" size={16} /> {card.currentPlan}
            </p>
          ) : null}
        </div>
        <div className="agent-meters">
          <Meter label="Mood" detail={card.mood >= 0.3 ? 'Good' : card.mood <= -0.3 ? 'Grumpy' : 'Okay'} value={(card.mood + 1) / 2} tone="sun" />
          <Meter label="Energy" value={card.energy} tone="leaf" />
          <Meter label="Stress" value={card.stress} tone="coral" />
          <Meter label="Fatigue" value={card.fatigue} tone="grape" />
          <span className="agent-next">
            <Icon name="clock" size={15} />
            {card.nextWakeAt ? `Next move ${fmtRelative(card.nextWakeAt, now)}` : 'No move planned'}
          </span>
        </div>
        <PokeBox personaId={personaId} name={shortName(card.name)} />
      </header>

      <div className="agent-cols">
        <div className="agent-left">
          <Panel title={live ? 'Live screen' : 'Screen'} icon={card.breakpoint === 'mobile' ? 'mobile' : 'desktop'} className="agent-screen">
            <LiveScreen session={session} breakpoint={card.breakpoint} />
            {session?.currentTaskGoal ? (
              <p className="screen-goal">
                <Icon name="target" size={16} /> {session.currentTaskGoal}
              </p>
            ) : null}
          </Panel>
          <Panel title="aa-app tasks" icon="list">
            {tasks === undefined ? <Spinner /> : null}
            {tasks?.length === 0 ? <Empty icon="desktop" title="No app tasks yet">When {shortName(card.name)} decides to use aa-app, each attempt shows up here step by step.</Empty> : null}
            {tasks && tasks.length > 0 ? (
              <div className="task-tabs">
                {tasks.map((t) => (
                  <button key={t._id} type="button" className={`task-tab status-${t.status} ${activeTask?._id === t._id ? 'is-active' : ''}`} onClick={() => setTaskId(t._id)}>
                    <span className="task-goal">{t.goal}</span>
                    <span className="task-meta">
                      {humanize(t.status)} · {fmtRelative(t.createdAt, now)}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
            {activeTask ? <TaskDetail task={activeTask} now={now} /> : null}
          </Panel>
        </div>

        <div className="agent-right">
          <Segmented<Tab>
            ariaLabel="Persona sections"
            value={tab}
            onChange={setTab}
            options={[
              { value: 'mind', label: 'Mind', icon: 'brain' },
              { value: 'wakes', label: 'Schedule', icon: 'clock' },
              { value: 'inbox', label: 'Inbox', icon: 'mail' },
              { value: 'chat', label: 'Chat', icon: 'chat' },
              { value: 'sheet', label: 'Sheet', icon: 'list' },
            ]}
          />
          {tab === 'mind' ? <MindTab personaId={personaId} cast={cast} now={now} /> : null}
          {tab === 'wakes' ? <WakesTab personaId={personaId} now={now} /> : null}
          {tab === 'inbox' ? <InboxTab personaId={personaId} now={now} /> : null}
          {tab === 'chat' ? <ChatLines personaId={personaId} now={now} /> : null}
          {tab === 'sheet' ? <Sheet persona={persona} /> : null}
        </div>
      </div>
    </div>
  );
}

function PokeBox({ personaId, name }: { personaId: Id<'personas'>; name: string }) {
  const { isAdmin } = useSession();
  const poke = useSimMutation(api.agents.loop.pokePersona);
  const toasts = useToasts();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  if (!isAdmin) return null;
  return (
    <form
      className="poke"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          await poke({ personaId, note: note.trim() || undefined });
          toasts.push(`${name} got a nudge.`, { tone: 'leaf', icon: 'hand' });
          setNote('');
        } catch (err) {
          toasts.push(err instanceof Error ? err.message : 'Could not poke.', { tone: 'danger', icon: 'alert' });
        } finally {
          setBusy(false);
        }
      }}
    >
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={`Whisper to ${name} (optional)`} maxLength={280} aria-label="Note for the poke" />
      <Button type="submit" icon="hand" tone="grape" disabled={busy}>
        Poke
      </Button>
    </form>
  );
}

function TaskDetail({ task, now }: { task: Doc<'appTasks'>; now: number }) {
  const steps = useSimQuery(api.browser.tasks.listTaskSteps, { taskId: task._id });
  return (
    <div className="task-detail">
      <div className="task-facts">
        <Chip tone={task.status === 'succeeded' ? 'leaf' : task.status === 'failed' || task.status === 'rejected' ? 'danger' : 'sky'}>{humanize(task.status)}</Chip>
        <Chip icon={task.breakpoint === 'mobile' ? 'mobile' : 'desktop'}>{task.breakpoint}</Chip>
        {task.startPath ? <Chip icon="pin">{task.startPath}</Chip> : null}
        <Chip>
          {task.stepCount}/{task.maxSteps} steps
        </Chip>
        {task.startedAt ? <Chip icon="clock">{fmtDuration((task.endedAt ?? now) - task.startedAt)}</Chip> : null}
        {task.costUsd > 0 ? <Chip tone="coral">{fmtUsdPrecise(task.costUsd)}</Chip> : null}
      </div>
      {task.rejectReason ? <p className="muted">Not started: {task.rejectReason}</p> : null}
      {task.result ? (
        <div className="task-result">
          <p>
            <b>{task.result.success ? 'Done.' : 'Gave up.'}</b> {task.result.summary}
          </p>
          {task.result.uxNotes.length > 0 ? (
            <ul className="bullets ux">
              {task.result.uxNotes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          ) : null}
          {task.result.bugs.length > 0 ? (
            <ul className="bullets bugs">
              {task.result.bugs.map((b, i) => (
                <li key={i}>
                  <b>{b.title}</b> {b.detail} {b.route ? <code>{b.route}</code> : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {steps === undefined ? <Spinner /> : null}
      <ol className="steps">
        {steps?.map((s) => (
          <li key={s._id} className={s.ok ? 'is-ok' : 'is-fail'}>
            {s.screenshotUrl ? (
              <a href={s.screenshotUrl} target="_blank" rel="noreferrer" className="step-shot">
                <img src={s.screenshotUrl} alt={`Step ${s.seq} screenshot`} loading="lazy" />
              </a>
            ) : (
              <span className="step-shot is-empty">{s.seq}</span>
            )}
            <div>
              <p className="step-thought">{s.thought}</p>
              <p className="step-action">
                <code>{s.action.type}</code> {s.action.target ? <span>{s.action.target}</span> : null} {s.action.text ? <q>{s.action.text}</q> : null}
              </p>
              <p className="step-meta">
                {fmtClock(s.at)} · {safePath(s.url)}
                {s.error ? <span className="step-error"> · {s.error}</span> : null}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function safePath(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname;
  } catch {
    return url;
  }
}

function MindTab({ personaId, cast, now }: { personaId: Id<'personas'>; cast: Cast; now: number }) {
  const events = useSimQuery(api.agents.feed.listFeed, { personaId, limit: 60 });
  return (
    <Panel>
      <FeedList events={events} cast={cast} now={now} showPersona={false} emptyText="No thoughts recorded yet." />
    </Panel>
  );
}

function WakesTab({ personaId, now }: { personaId: Id<'personas'>; now: number }) {
  const wakes = useSimQuery(api.agents.loop.listUpcomingWakes, { personaId, limit: 30 });
  return (
    <Panel>
      {wakes === undefined ? <Spinner /> : null}
      {wakes?.length === 0 ? <Empty icon="zzz" title="Nothing scheduled" /> : null}
      <ol className="wake-list">
        {wakes?.map((w) => (
          <li key={w._id} className={`wake status-${w.status}`}>
            <time>{fmtClock(w.at)}</time>
            <span className="wake-reason">{w.reason}</span>
            <span className="wake-meta">
              {humanize(w.source)} · {humanize(w.status)} · {fmtRelative(w.at, now)}
            </span>
            {w.gate ? (
              <span className="wake-gate" title="System-1 (Decider) verdict">
                <Icon name="brain" size={14} /> act {Math.round(w.gate.act * 100)}% · urgency {w.gate.urgency.toFixed(1)} · {humanize(w.gate.mode)} · {w.gate.latencyMs} ms
              </span>
            ) : null}
            {w.outcome ? <span className="wake-outcome">{w.outcome}</span> : null}
          </li>
        ))}
      </ol>
    </Panel>
  );
}

function InboxTab({ personaId, now }: { personaId: Id<'personas'>; now: number }) {
  const emails = useSimQuery(api.comms.mail.listEmails, { personaId, limit: 40 });
  return (
    <Panel>
      {emails === undefined ? <Spinner /> : <EmailList emails={emails} now={now} />}
    </Panel>
  );
}

function ChatLines({ personaId, now }: { personaId: Id<'personas'>; now: number }) {
  const messages = useSimQuery(api.comms.chat.listMessages, { limit: 120 });
  const channels = useSimQuery(api.comms.chat.listChannels, {});
  const mine = messages?.filter((m) => m.authorPersonaId === personaId || m.mentions.includes(personaId));
  const channelName = (id: Id<'channels'>) => channels?.find((c) => c._id === id)?.name ?? '#chat';
  return (
    <Panel>
      {messages === undefined ? <Spinner /> : null}
      {mine?.length === 0 ? <Empty icon="chat" title="Has not said a word" /> : null}
      <ul className="chat-lines">
        {mine?.map((m) => (
          <li key={m._id} className={m.authorPersonaId === personaId ? 'is-own' : 'is-mention'}>
            <span className="chat-chan">{channelName(m.channelId)}</span>
            <p>{m.text}</p>
            <time>{fmtRelative(m.at, now)}</time>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Sheet({ persona }: { persona: Doc<'personas'> | null | undefined }) {
  const [spoilers, setSpoilers] = useState(() => readLocal(STORAGE_KEYS.spoilers) === '1');
  if (persona === undefined) return <Spinner />;
  if (persona === null) return <Empty title="No character sheet" />;
  const scenario = PERSONAS.find((p) => p.key === persona.key);
  const toggleSpoilers = () => {
    writeLocal(STORAGE_KEYS.spoilers, spoilers ? null : '1');
    setSpoilers(!spoilers);
  };
  const b = persona.behavior;
  return (
    <Panel>
      <div className="sheet">
        <p className="sheet-bio">{persona.bio}</p>
        <dl className="sheet-facts">
          <dt>Age</dt>
          <dd>
            {persona.age} · {persona.pronouns}
          </dd>
          <dt>Personality</dt>
          <dd>{persona.personality}</dd>
          <dt>Voice</dt>
          <dd>{persona.voice}</dd>
          <dt>Shift</dt>
          <dd>
            {persona.schedule.shiftStart}–{persona.schedule.shiftEnd} · sleeps {persona.schedule.sleepStart}–{persona.schedule.sleepEnd}
            {persona.schedule.nightOwl ? ' · night owl' : ''}
          </dd>
          <dt>aa-app</dt>
          <dd>
            {persona.aaApp.role} · {persona.aaApp.appView === 'driver' ? 'driver view' : 'manage view'} · {persona.aaApp.breakpoint}
            {persona.aaApp.usesAssistant ? ' · uses the AI assistant' : ''}
          </dd>
          <dt>NFC card</dt>
          <dd>Taps in {Math.round(persona.nfcDiscipline * 100)}% of the time</dd>
        </dl>
        <SheetList title="Wants" items={persona.motivations} />
        <SheetList title="Goals" items={persona.goals} />
        <SheetList title="Flaws" items={persona.flaws} />
        <div className="sheet-secrets">
          <h3>
            Secrets{' '}
            <button type="button" className="link-btn" onClick={toggleSpoilers}>
              {spoilers ? 'Hide' : 'Reveal'}
            </button>
          </h3>
          <ul className={spoilers ? '' : 'is-blurred'} aria-hidden={!spoilers}>
            {persona.secrets.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>
        <h3>Behind the wheel</h3>
        <div className="sheet-behavior">
          <Meter compact label="Aggression" value={b.aggression} tone="coral" />
          <Meter compact label="Speeding" value={b.speeding} tone="sun" />
          <Meter compact label="Phone use" value={b.phoneUse} tone="grape" />
          <Meter compact label="Distraction" value={b.distraction} tone="sky" />
          <Meter compact label="Fatigue" value={b.fatigue} tone="grape" />
          <Meter compact label="Seatbelt on" value={b.seatbelt} tone="leaf" />
        </div>
        <h3>App duties</h3>
        <div className="sheet-duties">
          {(scenario?.appDuties ?? persona.appDuties).map((d) => (
            <Chip key={d}>{humanize(d)}</Chip>
          ))}
        </div>
      </div>
    </Panel>
  );
}

function SheetList({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <>
      <h3>{title}</h3>
      <ul className="bullets">
        {items.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </>
  );
}
